import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "orderbook__header" }

import { debounce } from 'lodash/fp';
import ResizeObserver from 'resize-observer-polyfill';
import { computed, ref, onBeforeUnmount, watch, onMounted, type PropType, toRefs } from 'vue';

import { orderformService } from '@exchange/libs/order/order-form/service/src';
import { OrderType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { useMyOrdersIndicators, AggregationOperation, OrderbookSide } from '@exchange/libs/orderbook/service/src';
import type { Orderbook } from '@exchange/libs/orderbook/service/src';
import { settingsService } from '@exchange/libs/settings/service/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

import OrderbookSeparator from './components/OrderbookSeparator.vue';
import { ORDERBOOK_RESIZE_DEBOUNCE_MS } from './constants.config';
import { LevelColumns, getColumns, getColumnTextAlignment } from './fast/OrderbookPricelevel';
import FastOrderbookSide from './fast/OrderbookPricelevels.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'Orderbook',
  props: {
  aggregationExecute: { type: Function as PropType<(operation: AggregationOperation, marketPrecision?: number | undefined) => number | undefined>, required: true },
  aggregationLevel: { type: [Number, String], required: true },
  aggregationRaisePossible: { type: Boolean, required: true },
  aggregationReducePossible: { type: Boolean, required: true },
  amountPrecision: { type: Number, required: true },
  doScrollAtMiddle: { type: Function as PropType<(additionalOffset?: number) => void>, required: false },
  hasAnimations: { type: Boolean, required: true },
  lastPrice: { type: Number, required: true },
  marketId: { type: String, required: true },
  marketPrecision: { type: Number, required: true },
  mini: { type: Boolean, default: false },
  msPerFrame: { type: Number, required: true },
  orderbook: { type: Object as PropType<Orderbook>, required: true },
  orderbookAddedTime: { type: Number, default: 0 },
  orderbookResubscribe: { type: Function as PropType<() => Promise<void>>, required: true },
  totalPrecision: { type: Number, default: 0 },
  useFastOrderbook: { type: Boolean, default: true },
},
  setup(__props, { expose: __expose }) {

const props = __props;

const { orderbook, marketId } = toRefs(props);

const asks = computed(() => props.orderbook.asks);
const bids = computed(() => props.orderbook.bids);
const spread = computed(() => props.orderbook.spread);
const asksMaxAmount = computed(() => Math.max(...asks.value.map((a) => a.amount)));
const bidsMaxTotal = computed(() => Math.max(...bids.value.map((b) => b.total)));
const spreadInBasePoints = computed(() => settingsService.settings.userSettings.orderbookSettings.spreadInBasePoints ?? false);

const columns = computed(() => getColumns(props.mini));
const orderType = computed(() => orderformService.data.type);
const root = ref<HTMLDivElement>();
const width = ref<number>(0);
let resizeObserver: ResizeObserver;
let onResize: (rect: DOMRectReadOnly) => void;

const { myOrderIndicators } = useMyOrdersIndicators({ orderbook, marketId });

const startObserving = () => {
  if (root.value && resizeObserver) {
    resizeObserver.observe(root.value);
  }
};

const recenter = () => {
  const additionalOffset = 0;

  props.doScrollAtMiddle?.(additionalOffset);
};

__expose({ recenter });

watch(root, startObserving);
watch(marketId, () => {
  recenter();
});

onMounted(() => {
  recenter();

  onResize = debounce(ORDERBOOK_RESIZE_DEBOUNCE_MS, (size: DOMRectReadOnly) => {
    width.value = size.width;
  });

  resizeObserver = new ResizeObserver((resizeEvents: Array<ResizeObserverEntry>) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const event of resizeEvents) {
      if (onResize) {
        onResize(event.contentRect);
      }
    }
  });

  startObserving();
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});

const itemClicked = (item: { key: LevelColumns; amount: string; price: string; total: string }) => {
  if (orderType.value === OrderType.MARKET) {
    if (item.key === LevelColumns.amount) {
      orderformService.setAmount(item.amount);
    }
  } else {
    switch (item.key) {
      case LevelColumns.amount:
        orderformService.setAmount(item.amount);
        break;
      case LevelColumns.price:
        orderformService.setStopPrice(item.price);
        orderformService.setLimitPrice(item.price);
        break;
      case LevelColumns.total:
        orderformService.setStopPrice(item.price);
        orderformService.setLimitPrice(item.price);
        orderformService.setTotal(item.total);
        break;
      default:
        logger.warn('no type specified');
        break;
    }
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: "orderbook"
  }, [
    _createElementVNode("div", _hoisted_1, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (header) => {
        return (_openBlock(), _createElementBlock("div", {
          key: header.lKey,
          class: _normalizeClass(["orderbook__header-value", `orderbook__header-value--align-${_unref(getColumnTextAlignment)({ column: header, mini: __props.mini })}`])
        }, _toDisplayString(_ctx.$t(header.lKey)), 3))
      }), 128))
    ]),
    (_openBlock(), _createBlock(FastOrderbookSide, {
      key: `ask-${__props.orderbookAddedTime}-${_unref(orderbook).currentAggregationLevel}-${__props.mini}`,
      class: "orderbook__pricelevels asks",
      side: _unref(OrderbookSide).ASK,
      columns: columns.value,
      mini: __props.mini,
      pricelevels: asks.value,
      "market-id": _unref(marketId),
      "amount-precision": __props.amountPrecision,
      "market-precision": __props.marketPrecision,
      "total-precision": __props.totalPrecision,
      "max-amount": asksMaxAmount.value,
      "my-order-indicators": _unref(myOrderIndicators),
      width: width.value,
      "ms-per-frame": __props.msPerFrame,
      onItemClicked: _cache[0] || (_cache[0] = (e) => itemClicked(e))
    }, null, 8, ["side", "columns", "mini", "pricelevels", "market-id", "amount-precision", "market-precision", "total-precision", "max-amount", "my-order-indicators", "width", "ms-per-frame"])),
    _createVNode(OrderbookSeparator, {
      class: "orderbook__separator",
      "current-spread": spread.value,
      "spread-in-bps": spreadInBasePoints.value,
      "last-matched-price": __props.lastPrice,
      "market-precision": __props.marketPrecision,
      "reduce-possible": __props.aggregationReducePossible,
      "raise-possible": __props.aggregationRaisePossible,
      level: __props.aggregationLevel,
      mini: __props.mini,
      onAggregate: __props.aggregationExecute
    }, null, 8, ["current-spread", "spread-in-bps", "last-matched-price", "market-precision", "reduce-possible", "raise-possible", "level", "mini", "onAggregate"]),
    (_openBlock(), _createBlock(FastOrderbookSide, {
      key: `bid-${__props.orderbookAddedTime}-${_unref(orderbook).currentAggregationLevel}-${__props.mini}`,
      class: "orderbook__pricelevels bids",
      side: _unref(OrderbookSide).BID,
      columns: columns.value,
      mini: __props.mini,
      pricelevels: bids.value,
      "market-id": _unref(marketId),
      "amount-precision": __props.amountPrecision,
      "market-precision": __props.marketPrecision,
      "total-precision": __props.totalPrecision,
      "max-amount": bidsMaxTotal.value,
      "my-order-indicators": _unref(myOrderIndicators),
      width: width.value,
      "ms-per-frame": __props.msPerFrame,
      onItemClicked: _cache[1] || (_cache[1] = (e) => itemClicked(e))
    }, null, 8, ["side", "columns", "mini", "pricelevels", "market-id", "amount-precision", "market-precision", "total-precision", "max-amount", "my-order-indicators", "width", "ms-per-frame"]))
  ], 512))
}
}

})